<script lang="tsx">
  import type { PropType, CSSProperties } from 'vue';

  import { computed, defineComponent, unref, toRef } from 'vue';
  import { BasicMenu } from '@/components/Menu';
  import { SimpleMenu } from '@/components/SimpleMenu';
  import { AppLogo } from '@/components/Application';
  import { MenuSearch } from '@/components/MenuSearch';

  import { MenuModeEnum, MenuSplitTyeEnum } from '@/enums/menuEnum';

  import { useMenuSetting } from '@/hooks/setting/useMenuSetting';
  import { ScrollContainer } from '@/components/Container';

  import { useGo } from '@/hooks/web/usePage';
  import { useSplitMenu } from './useLayoutMenu';
  import { openWindow } from '@/utils';
  import { propTypes } from '@/utils/propTypes';
  import { isUrl, StorageUtils } from 'xmgoing-utils';
  import { useRootSetting } from '@/hooks/setting/useRootSetting';
  import { useAppInject } from '@/hooks/web/useAppInject';
  import { useDesign } from '@/hooks/web/useDesign';
  import {useHeaderSetting} from "@/hooks/setting/useHeaderSetting";
  import eventBus from "@/events/eventBus";
  // import { log } from 'console';

  export default defineComponent({
    name: 'LayoutMenu',
    props: {
      theme: propTypes.oneOf(['light', 'dark']),

      splitType: {
        type: Number as PropType<MenuSplitTyeEnum>,
        default: MenuSplitTyeEnum.NONE,
      },

      isHorizontal: propTypes.bool,
      // menu Mode
      menuMode: {
        type: [String] as PropType<Nullable<MenuModeEnum>>,
        default: '',
      },
    },
    setup(props) {
      const go = useGo();

      const route = useRoute()

      const isSimplFlowDeal = computed(() => {
        if(unref(route).name&&typeof unref(route).name==='string'){
          return unref(route).name?.includes('simplFlowDeal')
        }
      })

      const {
        getMenuMode,
        getMenuType,
        getMenuTheme,
        getCollapsed,
        getCollapsedShowTitle,
        getAccordion,
        getIsHorizontal,
        getIsSidebarType,
        getSplit,
      } = useMenuSetting();

      const {
        getShowSystemTitle,
      } = useHeaderSetting();

      const { getShowLogo,getNotShowAppMenuFlag } = useRootSetting();

      const { prefixCls } = useDesign('layout-menu');

      const { menusRef } = useSplitMenu(toRef(props, 'splitType'),getNotShowAppMenuFlag);

      const position=StorageUtils.getSessionItem("loginUserType");

      const { getIsMobile } = useAppInject();

      const getComputedMenuMode = computed(() => {
        if (unref(getIsMobile)) {
          return MenuModeEnum.INLINE;
        }
        // 头部横向菜单强制 horizontal，避免 header 传入 null 时落到默认 inline
        if (props.isHorizontal) {
          return MenuModeEnum.HORIZONTAL;
        }
        return props.menuMode || unref(getMenuMode);
      });

      const getComputedMenuTheme = computed(() => props.theme || unref(getMenuTheme));

      const getIsShowLogo = computed(() => unref(getShowLogo) && unref(getIsSidebarType));

      const getUseScroll = computed(() => {
        return (
          !unref(getIsHorizontal) &&
          (unref(getIsSidebarType) ||
            props.splitType === MenuSplitTyeEnum.LEFT ||
            props.splitType === MenuSplitTyeEnum.NONE)
        );
      });

      const getLogoClass = computed(() => {
        return [
          `${prefixCls}-logo`,
          unref(getComputedMenuTheme),
          {
            [`${prefixCls}--mobile`]: unref(getIsMobile),
          },
        ];
      });

      const getCommonProps = computed(() => {
        const menus = unref(menusRef);
        return {
          menus,
          beforeClickFn: beforeMenuClickFn,
          items: menus,
          theme: unref(getComputedMenuTheme),
          accordion: unref(getAccordion),
          collapse: unref(getCollapsed),
          collapsedShowTitle: unref(getCollapsedShowTitle),
          onMenuClick: handleMenuClick,
        };
      });
      /**
       * click menu
       * @param menu
       */

      function handleMenuClick(path: string) {
        go(path, unref(isSimplFlowDeal));
      }

      onMounted(() => {
        eventBus.off('menuClick', handleMenuClick);
        eventBus.on('menuClick', handleMenuClick);
      })

      /**
       * before click menu
       * @param menu
       */
      async function beforeMenuClickFn(path: string) {
        if (!isUrl(path)) {
          return true;
        }

        openWindow(path);
        return false;
      }

      function renderHeader() {
        if (!unref(getIsShowLogo) && !unref(getIsMobile)) return null;

        return (
          <AppLogo
            showTitle={unref(getCollapsed)&&unref(getShowSystemTitle)}
            class={unref(getLogoClass)}
            theme={unref(getComputedMenuTheme)}
          />
        );
      }

      function renderMenuSearch () {
        if (position !== 'user') {
          return null
        }

        // 头部横向菜单主题下搜索框固定 180px，避免占满顶部菜单位置
        return <MenuSearch width={props.isHorizontal ? 180 : undefined} />
      }

      function renderMenu() {
        let { menus, ...menuProps } = unref(getCommonProps);

        if (!menus || !menus.length) return null;

        return !props.isHorizontal ? (
          //纵向菜单
          <SimpleMenu {...menuProps} isSplitMenu={unref(getSplit)} items={menus} />
        ) : (
          //横向菜单
          <BasicMenu
            {...(menuProps as any)}
            isHorizontal={props.isHorizontal}
            type={unref(getMenuType)}
            showLogo={unref(getIsShowLogo)}
            mode={unref(getComputedMenuMode as any)}
            items={menus}
          />
        );
      }

      return () => {
        const content = props.isHorizontal ? (
          <>
            {renderHeader()}
            {unref(getUseScroll) ? (
              <ScrollContainer>{() => renderMenu()}</ScrollContainer>
            ) : (
              renderMenu()
            )}
            {renderMenuSearch()}
          </>
        ) : (
          <>
            {renderHeader()}
            {renderMenuSearch()}
            {unref(getUseScroll) ? (
              <ScrollContainer>{() => renderMenu()}</ScrollContainer>
            ) : (
              renderMenu()
            )}
          </>
        );

        // 头部横向菜单：菜单在左，搜索框在右
        if (props.isHorizontal) {
          return <div class={`${prefixCls}-horizontal`}>{content}</div>;
        }

        return content;
      };
    },
  });
</script>
<style lang="less">
  @prefix-cls: ~'@{namespace}-layout-menu';
  @logo-prefix-cls: ~'@{namespace}-app-logo';
  @basic-menu-prefix-cls: ~'@{namespace}-basic-menu';

  .@{prefix-cls} {
    &-logo {
      height: @header-height;
      padding: 10px 4px 10px 10px;

      img {
        width: @logo-width;
        height: @logo-width;
      }
    }

    &--mobile {
      .@{logo-prefix-cls} {
        &__title {
          opacity: 100%;
        }
      }
    }

    &-horizontal {
      display: flex;
      align-items: center;
      height: 100%;
      min-width: 0;
      width: 100%;

      .menu-search {
        flex: 0 0 180px;
        width: 180px;
        padding: 0 8px;
      }

      .@{basic-menu-prefix-cls} {
        flex: 1;
        min-width: 0;
        width: auto;
      }
    }
  }
</style>
